import { Button, createTableColumn, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, makeStyles, mergeClasses, TableColumnDefinition, Text, Tooltip } from '@fluentui/react-components';
import { ArrowDownloadFilled, DeleteRegular, StarFilled } from '@fluentui/react-icons';
import { endsWith } from 'es-toolkit/compat';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { NavLink } from 'react-router';
import { useStores } from '../../../common/hooks';
import { NominationSummary } from '../../../models';
import { RoutePaths } from '../../../route-paths';
import { PageTitle } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import Loading from '../../shared-components/Loading';
import './NominationSummaries.scss';

const winnerRoles = ['Winner', 'Finalist'] as const;

const useStyle = makeStyles({
  tableRow: { border: 0 },
});

interface NominationSummariesProps {
  onDelete: (nominationId: number) => void;
  onDownload: (nominationId: number) => void;
  isNominationWindowClosed: boolean;
  isLoadingNomination: boolean;
}

const NominationSummariesImpl: React.FC<NominationSummariesProps> = (props) => {
  const {
    onDelete,
    onDownload,
    isNominationWindowClosed,
    isLoadingNomination,
  } = props;

  const {
    userStore,
    awardStore,
    nominationDashboardPageUiStore,
  } = useStores();

  const classes = useStyle();

  const currentUserId = userStore.userInfo?.userId ?? 0;
  const canViewStatus = awardStore.isWinnersWindowAlreadyOpen || endsWith((userStore.userInfo?.email ?? '').toLowerCase(), '@microsoft.com');

  const getStatusClassName = (role?: string): string => {
    if (!role || !canViewStatus) return '';
    if (role.includes('Winner')) return 'winner';
    if (role.includes('Finalist')) return 'finalist';
    return '';
  }

  const columns: TableColumnDefinition<NominationSummary>[] = [
    createTableColumn<NominationSummary>({
      columnId: 'role',
      renderHeaderCell: () => '',
      renderCell: (item) => (
        <DataGridCell
          className='role-col'
        >
          {
            canViewStatus
            && winnerRoles.some((role) => item.role?.includes(role))
            && (
              <Tooltip
                content={item.role ?? ''}
                relationship='label'
              >
                <StarFilled
                  color={item.role?.startsWith('Winner') ? '#FFB900' : '#BDD8E3'}
                />
              </Tooltip>
            )
          }
        </DataGridCell>
      ),
    }),
    createTableColumn<NominationSummary>({
      columnId: 'nomination-id',
      renderHeaderCell: () => 'Nomination ID',
      renderCell: (item) => {
        const canEditNomination = item.createdBy.userId === currentUserId && !isNominationWindowClosed && item.status !== 'Submitted';
        return (
          <DataGridCell
            focusMode={canEditNomination ? 'none' : 'cell'}
            className='nomination-id-col'
          >
            {
              canEditNomination
                ? (
                  <NavLink
                    to={`${RoutePaths.Nominations}/${item.nominationId}`}
                    end
                  >
                    <Text
                      size={200}
                      weight='semibold'
                    >
                      {item.nominationNumber}
                    </Text>
                  </NavLink>

                ) : (
                  <Text
                    size={200}
                    weight='semibold'
                  >
                    {item.nominationNumber}
                  </Text>
                )
            }
          </DataGridCell>
        )
      },
    }),
    createTableColumn<NominationSummary>({
      columnId: 'award',
      renderHeaderCell: () => 'Award',
      renderCell: (item) => (
        <DataGridCell
          className='award-col'
        >
          <Text
            size={200}
          >
            {item.award.title}
          </Text>
        </DataGridCell>
      ),
    }),
    createTableColumn<NominationSummary>({
      columnId: 'solution-name',
      renderHeaderCell: () => 'Solution Name',
      renderCell: (item) => (
        <DataGridCell
          className='solution-name-col'
        >
          <Text
            size={200}
          >
            {item.name}
          </Text>
        </DataGridCell>
      ),
    }),
    createTableColumn<NominationSummary>({
      columnId: 'owner',
      renderHeaderCell: () => 'Owner',
      renderCell: (item) => (
        <DataGridCell
          className='owner-col'
        >
          <Text
            size={200}
          >
            {`${item.createdBy.lastName}, ${item.createdBy.firstName}`}
          </Text>
        </DataGridCell>
      ),
    }),
    createTableColumn<NominationSummary>({
      columnId: 'partner-id',
      renderHeaderCell: () => 'Partner ID',
      renderCell: (item) => (
        <DataGridCell
          className='partner-id-col'
        >
          <Text
            size={200}
          >
            {item.mpnId}
          </Text>
        </DataGridCell>
      ),
    }),
    createTableColumn<NominationSummary>({
      columnId: 'status',
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <DataGridCell
          className='status-col'
        >
          <Text
            size={200}
            weight='semibold'
          >
            {item.status}
          </Text>
        </DataGridCell>
      ),
    }),
    createTableColumn<NominationSummary>({
      columnId: 'actions',
      renderHeaderCell: () => '',
      renderCell: (item) => {
        const canEditNomination = item.createdBy.userId === currentUserId && !isNominationWindowClosed && item.status !== 'Submitted';
        const canDeleteNomination = item.status === 'Incomplete' && canEditNomination;
        return (
          <DataGridCell
            className='actions-col'
            focusMode='group'
          >
            {
              canDeleteNomination && (
                <Button
                  appearance='transparent'
                  size='small'
                  icon={<DeleteRegular color='#0078D4' />}
                  onClick={() => onDelete(item.nominationId)}
                  className='delete-button'
                  aria-label='delete'
                />
              )
            }
            <Button
              appearance='transparent'
              size='small'
              icon={<ArrowDownloadFilled color='#0078D4' />}
              onClick={() => onDownload(item.nominationId)}
              aria-label='download'
            />
          </DataGridCell>
        )
      },
    }),
  ];

  return (
    <FlexBox
      direction='column'
      as='section'
      className='nomination-summaries'
    >
      <PageTitle
        title="Your company's nominations"
        textHeaderTag='h2'
        titleSize={700}
      />
      <div
        className='nomination-summaries__table'
      >
        {isLoadingNomination && (
          <Loading isFullScreen={false} />
        )}
        {
          nominationDashboardPageUiStore.nominationSummaries?.length > 0 ? (
            <DataGrid
              items={nominationDashboardPageUiStore.nominationSummaries}
              columns={columns}
              className='table-main'
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell, columnId }) => (
                    <DataGridHeaderCell
                      key={columnId}
                      className={`${columnId}-col`}
                    >
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<NominationSummary>>
                {({ item, rowId }) => (
                  <DataGridRow<NominationSummary>
                    key={rowId}
                    className={mergeClasses(classes.tableRow, getStatusClassName(item.role))}
                  >
                    {({ renderCell }) => renderCell(item)}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          ) : (
            <Text
              as='p'
              block
            >
              There are no nominations associated with your account.
            </Text>
          )
        }
      </div>
    </FlexBox>
  );
};

export const NominationSummaries = observer(NominationSummariesImpl);
